import React from 'react';
import { StyleSheet, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import Animated, { SharedValue, clamp, useAnimatedStyle, useDerivedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation } from '@react-navigation/native';

import { CloseIcon } from '@/assets/icons/close';
import { Box } from '@/components/lib/components';
import { CircleBottomGlow } from '@/feature/promohub/components/CircleBottomGlow';
import { CircleTopGlow } from '@/feature/promohub/components/CircleTopGlow';
import { CLOSE_ICON_PADDING, CLOSE_ICON_SIZE } from '@/feature/promohub/utils/promotions-utils';
import { PromotionThemeDocument } from '@/types/prismic.generated';

import { usePromoTheme } from '../hooks/use-promo-theme';

const styles = StyleSheet.create({
    navigationContainer: {
        position: 'absolute',
        zIndex: 1,
        top: 0,
        width: '100%',
        overflow: 'hidden',
    },
    navigationBackground: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: -1,
    },
    topGlowContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    },
    bottomGlowContainer: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: -1,
    },
    closeIcon: {
        paddingHorizontal: 16,
        width: '100%',
        paddingVertical: CLOSE_ICON_PADDING,
    },
});

interface PromoHeaderProps {
    theme: PromotionThemeDocument['data'];
    headerHeight: SharedValue<number>;
    scrollY: SharedValue<number>;
}

export const PromoHeader = ({ theme, headerHeight, scrollY }: PromoHeaderProps) => {
    const { width } = useWindowDimensions();
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();

    const { background_color, bottom_glow_color, top_glow_color, navigation_button_color } = usePromoTheme(theme);

    const navigationHeight = insets.top + 2 * CLOSE_ICON_PADDING + CLOSE_ICON_SIZE;

    const opacityValue = useDerivedValue(() => {
        return scrollY.value >= 160 ? 1 : 0;
    });

    const navigationBackground = useAnimatedStyle(() => {
        return {
            opacity: opacityValue.value,
        };
    });

    const outerBottomGlowAnimation = useAnimatedStyle(() => {
        return {
            transform: [{ translateY: -clamp(scrollY.value, 0, headerHeight.value - navigationHeight) }],
        };
    });

    return (
        <Animated.View style={[styles.navigationContainer]}>
            <Animated.View
                style={[{ backgroundColor: background_color }, navigationBackground, styles.navigationBackground]}
            />
            <Animated.View style={[styles.topGlowContainer, navigationBackground]}>
                <Box width={width} height={headerHeight.value}>
                    <CircleTopGlow color={top_glow_color} headerHeight={headerHeight} />
                </Box>
            </Animated.View>
            <Animated.View style={{ paddingTop: insets.top }}>
                <View style={[styles.closeIcon]}>
                    <TouchableOpacity hitSlop={20} activeOpacity={0.8} onPress={() => navigation.goBack()}>
                        <CloseIcon color={navigation_button_color} width={CLOSE_ICON_SIZE} height={CLOSE_ICON_SIZE} />
                    </TouchableOpacity>
                </View>
            </Animated.View>
            <Animated.View style={[styles.bottomGlowContainer, outerBottomGlowAnimation]}>
                <Box width={width} height={headerHeight.value}>
                    <CircleBottomGlow headerHeight={headerHeight} color={bottom_glow_color} />
                </Box>
            </Animated.View>
        </Animated.View>
    );
};
